import { Box, Text, InlineStack, BlockStack, Badge } from '@shopify/polaris';

interface DiffField {
  from: unknown;
  to: unknown;
}

/**
 * Renders a field-level diff object as read in the `audit_logs.diff` column.
 *
 * Shapes supported:
 *   { field: { from, to } }                    — typical update
 *   { created: { …row } }                      — create action
 *   { deleted: { …row } }                      — delete action
 *   { bolt_patterns: { from, to } }            — also any nested key
 */
export function DiffRenderer({ diff }: { diff: Record<string, unknown> }) {
  if (!diff || typeof diff !== 'object') {
    return <Text as="p" variant="bodySm" tone="subdued">no diff</Text>;
  }

  const entries = Object.entries(diff);
  if (entries.length === 0) {
    return <Text as="p" variant="bodySm" tone="subdued">(no field changes)</Text>;
  }

  return (
    <BlockStack gap="200">
      {entries.map(([key, value]) => (
        <DiffLine key={key} field={key} value={value} />
      ))}
    </BlockStack>
  );
}

function DiffLine({ field, value }: { field: string; value: unknown }) {
  if (field === 'created' || field === 'deleted') {
    return (
      <Box padding="300" background="bg-surface-secondary" borderRadius="200">
        <BlockStack gap="100">
          <InlineStack gap="200" blockAlign="center">
            <Badge tone={field === 'created' ? 'success' : 'critical'}>{field}</Badge>
            <Text as="span" variant="bodySm" tone="subdued">(full row)</Text>
          </InlineStack>
          <code style={{ fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
            {truncate(safeStringify(value), 600)}
          </code>
        </BlockStack>
      </Box>
    );
  }
  if (isFromTo(value)) {
    return (
      <Box padding="200" borderWidth="025" borderColor="border" borderRadius="100">
        <InlineStack gap="200" blockAlign="start" wrap={false}>
          <Text as="span" variant="bodyMd" fontWeight="semibold">{field}</Text>
          <Box>
            <InlineStack gap="200" blockAlign="center" wrap={false}>
              <code style={{ color: '#bf0711' }}>{renderScalar(value.from)}</code>
              <Text as="span" variant="bodySm" tone="subdued">→</Text>
              <code style={{ color: '#008060' }}>{renderScalar(value.to)}</code>
            </InlineStack>
          </Box>
        </InlineStack>
      </Box>
    );
  }
  return (
    <Box padding="200">
      <InlineStack gap="200">
        <Text as="span" variant="bodyMd" fontWeight="semibold">{field}</Text>
        <code style={{ fontSize: 11 }}>{truncate(safeStringify(value), 200)}</code>
      </InlineStack>
    </Box>
  );
}

function isFromTo(v: unknown): v is DiffField {
  return Boolean(v && typeof v === 'object' && 'from' in (v as any) && 'to' in (v as any));
}

function renderScalar(v: unknown): string {
  if (v === null || v === undefined) return 'null';
  if (typeof v === 'object') return truncate(safeStringify(v), 80);
  return String(v);
}

function safeStringify(v: unknown): string {
  try { return JSON.stringify(v); } catch { return String(v); }
}

function truncate(s: string, n: number): string {
  return s.length <= n ? s : s.slice(0, n) + '…';
}
